import { useMemo } from "react";
import { Cloud, CloudRain, Moon, Sun, type LucideIcon } from "lucide-react";

import { formatRelativeTime } from "../../dateFormatting";
import { countryName } from "../../i18n/country-name";
import { useLanguage } from "../../i18n/language-provider";
import {
    EXAMPLE_MARKET,
    EXAMPLE_MIN_STAY,
    EXAMPLE_RATE_PLANS,
    exampleNightPrice,
    linkedPlanPrice,
    marketScalePosition,
    nextHolidays,
    SYNC_TICK_MINUTES,
    syncTicks,
} from "./featureSketchModel";
import {
    PREVIEW_COMPETITORS_PCT,
    PREVIEW_ROOMS,
    PREVIEW_WEATHER_PCT,
    previewNights,
    type PreviewWeather,
} from "./pricePreviewModel";
import { adjustmentTone, capitalise, PREVIEW_CURRENCY, withTrueMinus } from "./previewFormat";

/**
 * The small sketches the welcome page's feature tiles carry: the product doing
 * what the tile says, drawn with the product's own tokens and, wherever it has
 * them, its own words. Their numbers are worked examples
 * (`featureSketchModel.ts`), the same example the hero's price preview shows.
 */

interface SketchProps {
    /** The moment the sketch counts from. */
    now: Date;
}

function useMoney(locale: string) {
    return useMemo(
        () => new Intl.NumberFormat(locale, { style: "currency", currency: PREVIEW_CURRENCY, maximumFractionDigits: 0 }),
        [locale],
    );
}

function useSignedPercent(locale: string) {
    return useMemo(() => {
        const format = new Intl.NumberFormat(locale, {
            style: "percent",
            maximumFractionDigits: 0,
            signDisplay: "exceptZero",
        });
        return (pct: number) => withTrueMinus(format.format(pct / 100));
    }, [locale]);
}

// ---------------------------------------------------------------------------
// One night's price as it sold
// ---------------------------------------------------------------------------

/** One Saturday night's price, in the steps it moved by, over the 60 days it sold. */
const HISTORY_DAYS = 60;
const HISTORY_STEPS: { daysOut: number; price: number }[] = [
    { daysOut: 60, price: 2850 },
    { daysOut: 44, price: 3000 },
    { daysOut: 31, price: 3300 },
    { daysOut: 19, price: 3450 },
    { daysOut: 9, price: 3750 },
    { daysOut: 2, price: 3600 },
];
/** When each room sold, in days before arrival. */
const HISTORY_BOOKINGS = [50, 37, 24, 12, 6];
const HISTORY_FLOOR = 2700;
const HISTORY_CEILING = 3900;

const SKETCH_W = 600;
const SKETCH_H = 200;
const SKETCH_PAD = 16;

function sketchX(daysOut: number): number {
    return SKETCH_PAD + ((HISTORY_DAYS - daysOut) / HISTORY_DAYS) * (SKETCH_W - 2 * SKETCH_PAD);
}

function sketchY(price: number): number {
    const share = (price - HISTORY_FLOOR) / (HISTORY_CEILING - HISTORY_FLOOR);
    return SKETCH_H - SKETCH_PAD - share * (SKETCH_H - 2 * SKETCH_PAD);
}

/** The price that stood on a day: the last step taken at or before it. */
function priceOn(daysOut: number): number {
    let price = HISTORY_STEPS[0].price;
    for (const step of HISTORY_STEPS) if (step.daysOut >= daysOut) price = step.price;
    return price;
}

/**
 * The Price history tab in miniature: a step line, because between two moves
 * the price did not change, and a diamond on the step that stood when each
 * room sold. Its numbers are a worked example, stated in text beside it.
 *
 * It stretches to whatever height its tile leaves: the line is an SVG drawn
 * without preserving its aspect ratio, which a line of horizontal and
 * vertical steps survives untouched (its stroke kept at screen width), and
 * the diamonds are placed over it in HTML, so they stay square.
 */
export function PriceHistorySketch() {
    const { t, locale } = useLanguage();
    const money = useMoney(locale);
    const first = HISTORY_STEPS[0].price;
    const peak = Math.max(...HISTORY_STEPS.map((step) => step.price));

    const path = HISTORY_STEPS.reduce((d, step, i) => {
        const x = sketchX(step.daysOut).toFixed(1);
        const y = sketchY(step.price).toFixed(1);
        return i === 0 ? `M${x} ${y}` : `${d} H${x} V${y}`;
    }, "").concat(` H${sketchX(0).toFixed(1)}`);

    return (
        <figure className="flex flex-1 flex-col rounded-md border bg-background p-4">
            <figcaption className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs">
                <span className="font-medium text-foreground">{t.welcome.priceChartCaption}</span>
                <span className="flex items-center gap-3 text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                        <span aria-hidden="true" className="h-0.5 w-4 rounded-full bg-chart-1" />
                        {t.welcome.priceChartPrice}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span aria-hidden="true" className="size-2 rotate-45 bg-chart-2" />
                        {t.welcome.priceChartBooking}
                    </span>
                </span>
            </figcaption>
            <p className="mt-1 font-mono text-sm">
                {money.format(first)} → {money.format(peak)}
            </p>
            <div
                role="img"
                aria-label={`${t.welcome.priceChartCaption}: ${money.format(first)} → ${money.format(peak)}`}
                className="relative mt-3 min-h-36 flex-1"
            >
                <svg
                    aria-hidden="true"
                    viewBox={`0 0 ${SKETCH_W} ${SKETCH_H}`}
                    preserveAspectRatio="none"
                    className="absolute inset-0 h-full w-full overflow-visible"
                >
                    <line
                        x1={SKETCH_PAD}
                        x2={SKETCH_W - SKETCH_PAD}
                        y1={SKETCH_H}
                        y2={SKETCH_H}
                        stroke="var(--border)"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                    />
                    <line
                        x1={sketchX(0)}
                        x2={sketchX(0)}
                        y1={0}
                        y2={SKETCH_H}
                        stroke="var(--border)"
                        strokeWidth="1"
                        strokeDasharray="3 4"
                        vectorEffect="non-scaling-stroke"
                    />
                    <path
                        d={path}
                        fill="none"
                        stroke="var(--color-chart-1)"
                        strokeWidth="2.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                    />
                </svg>
                {HISTORY_BOOKINGS.map((daysOut) => (
                    <span
                        key={daysOut}
                        aria-hidden="true"
                        className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-chart-2 ring-2 ring-background"
                        style={{
                            left: `${(sketchX(daysOut) / SKETCH_W) * 100}%`,
                            top: `${(sketchY(priceOn(daysOut)) / SKETCH_H) * 100}%`,
                        }}
                    />
                ))}
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
                <span>{t.welcome.priceChartStart.replace("{days}", String(HISTORY_DAYS))}</span>
                <span>{t.welcome.priceChartArrival}</span>
            </div>
        </figure>
    );
}

// ---------------------------------------------------------------------------
// The connection: the rail's sync chip
// ---------------------------------------------------------------------------

const SYNC_PRICES_SENT = 186;

/**
 * The rail's sync chip, on the rail's own dark surface and in its own words:
 * when the reservations were last read, how many prices went out, and when
 * the next run is. It counts from the same tick as the monitor beside it.
 * It names no property management system: the public page names none
 * (8 Oct 2026), though the chip in the product names the one it syncs with.
 */
export function SyncSketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const { last, next } = syncTicks(now);
    const checked = formatRelativeTime(last, locale, now);

    return (
        <div data-testid="sync-sketch" className="rounded-md bg-sidebar p-3 text-sidebar-foreground">
            <div className="flex items-start gap-2.5">
                {/* The chip's own "ok" mark (DOT_CLASS in PmsSyncStatus.tsx). */}
                <span aria-hidden="true" className="mt-1.5 size-2 shrink-0 rounded-full bg-success" />
                <div className="min-w-0">
                    <p className="text-sm font-medium">{t.syncStatus.state.good}</p>
                    <p className="text-xs text-sidebar-foreground/70">{checked}</p>
                </div>
            </div>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-t border-sidebar-border pt-2 text-xs">
                <dt className="text-sidebar-foreground/70">{t.syncStatus.flow.inbound}</dt>
                <dd className="text-right">{checked}</dd>
                <dt className="text-sidebar-foreground/70">{t.syncStatus.flow.outbound}</dt>
                <dd className="text-right">{t.syncStatus.prices.replace("{count}", String(SYNC_PRICES_SENT))}</dd>
                <dt className="text-sidebar-foreground/70">{t.syncStatus.flow.next}</dt>
                <dd className="text-right">{formatRelativeTime(next, locale, now)}</dd>
            </dl>
        </div>
    );
}

// ---------------------------------------------------------------------------
// The half-hourly check: the booking pace monitor
// ---------------------------------------------------------------------------

/**
 * The Strategies page's Booking Pace Monitor in miniature, in that card's own
 * words: the last check, the next one a tick later, and the sentence saying
 * how often the system looks.
 */
export function MonitorSketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const time = useMemo(() => new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }), [locale]);
    const { last, next } = syncTicks(now);

    return (
        <div data-testid="monitor-sketch" className="rounded-md border bg-background p-3">
            <p className="flex items-center gap-2 text-sm font-medium">
                <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-success" />
                {t.strategies.monitoringActive}
            </p>
            <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
                <dt className="text-muted-foreground">{t.strategies.lastPoll}</dt>
                <dd className="text-right font-mono">{time.format(last)}</dd>
                <dt className="text-muted-foreground">{t.strategies.nextPoll}</dt>
                <dd className="text-right font-mono">{time.format(next)}</dd>
            </dl>
            <p className="mt-2 border-t pt-2 text-[11px] leading-relaxed text-muted-foreground">
                {t.strategies.pollingDescription.replace("{frequency}", t.syncCadence[SYNC_TICK_MINUTES])}
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// The market: the competitors' median beside the property's own price
// ---------------------------------------------------------------------------

/**
 * The example night's price on a scale with the market's median: the same
 * night the hero explains, where the competitors are the cheaper ones and the
 * price gave way by the percentage printed under it.
 */
export function CompetitorSketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const money = useMoney(locale);
    const signedPct = useSignedPercent(locale);
    const price = exampleNightPrice(now);
    const { median } = EXAMPLE_MARKET;
    const moved = PREVIEW_COMPETITORS_PCT.cheaper;

    return (
        <div data-testid="competitor-sketch" className="rounded-md border bg-background p-3">
            <div
                role="img"
                aria-label={`${t.welcome.marketSketchMedian}: ${money.format(median)}. ${t.welcome.marketSketchYours}: ${money.format(price)}.`}
                className="relative h-5"
            >
                <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted" />
                <span
                    className="absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-chart-2"
                    style={{ left: `${marketScalePosition(median)}%` }}
                />
                <span
                    className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-chart-1 ring-2 ring-background"
                    style={{ left: `${marketScalePosition(price)}%` }}
                />
            </div>
            <dl className="mt-2 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 text-xs">
                <dt className="flex items-center gap-2 text-muted-foreground">
                    <span aria-hidden="true" className="mx-1 h-3 w-0.5 shrink-0 bg-chart-2" />
                    {t.welcome.marketSketchMedian}
                </dt>
                <dd className="text-right font-mono">{money.format(median)}</dd>
                <dt className="flex items-center gap-2 text-muted-foreground">
                    <span aria-hidden="true" className="size-2 shrink-0 rotate-45 bg-chart-1" />
                    {t.welcome.marketSketchYours}
                </dt>
                <dd className="text-right font-mono">{money.format(price)}</dd>
            </dl>
            <p className="mt-2 flex items-baseline justify-between gap-3 border-t pt-2 text-[11px] text-muted-foreground">
                <span>{t.welcome.previewCompetitors.cheaper}</span>
                <span className={`font-mono ${adjustmentTone(moved)}`}>{signedPct(moved)}</span>
            </p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// The weather: the example week's forecast
// ---------------------------------------------------------------------------

const WEATHER_ICON: Record<PreviewWeather, LucideIcon> = {
    sunny: Sun,
    neutral: Cloud,
    rainy: CloudRain,
};

/**
 * The forecast for the week the hero's preview prices, with what each day's
 * weather moves the price by. A day is named in full for a screen reader; the
 * strip itself shows the weekday, the forecast's mark and the percentage.
 */
export function WeatherSketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const signedPct = useSignedPercent(locale);
    const weekdays = useMemo(
        () => ({
            short: new Intl.DateTimeFormat(locale, { weekday: "short" }),
            long: new Intl.DateTimeFormat(locale, { weekday: "long" }),
        }),
        [locale],
    );

    return (
        <ul
            data-testid="weather-sketch"
            aria-label={t.welcome.weatherSketchLabel}
            className="grid grid-cols-7 rounded-md border bg-background py-2.5 text-center"
        >
            {previewNights(now).map((night) => {
                const Icon = WEATHER_ICON[night.weather];
                const pct = PREVIEW_WEATHER_PCT[night.weather];
                const day = capitalise(weekdays.long.format(night.date), locale);
                return (
                    <li
                        key={night.date.toISOString()}
                        aria-label={`${day}: ${t.welcome.previewWeather[night.weather]} ${signedPct(pct)}`}
                        className="flex min-w-0 flex-col items-center gap-1"
                    >
                        <span aria-hidden="true" className="text-[11px] text-muted-foreground">
                            {capitalise(weekdays.short.format(night.date), locale)}
                        </span>
                        <Icon aria-hidden="true" className="size-4 text-foreground/70" />
                        <span aria-hidden="true" className={`font-mono text-[11px] ${adjustmentTone(pct)}`}>
                            {signedPct(pct)}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

// ---------------------------------------------------------------------------
// Holidays: the next public holidays, here and where the guests come from
// ---------------------------------------------------------------------------

/** How many holidays the sketch lists. */
const HOLIDAYS_SHOWN = 3;

/** The next public holidays, each with the countries that keep it, named in the reader's language. */
export function HolidaySketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const date = useMemo(() => new Intl.DateTimeFormat(locale, { day: "numeric", month: "numeric" }), [locale]);

    return (
        <ul
            data-testid="holiday-sketch"
            aria-label={t.welcome.holidaySketchLabel}
            className="divide-y divide-border rounded-md border bg-background text-xs"
        >
            {nextHolidays(now, HOLIDAYS_SHOWN).map((holiday) => (
                <li key={holiday.date.toISOString()} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
                    <span className="shrink-0 font-mono">{date.format(holiday.date)}</span>
                    <span className="min-w-0 truncate text-muted-foreground">
                        {holiday.countries.map((code) => countryName(locale, code) ?? code).join(", ")}
                    </span>
                </li>
            ))}
        </ul>
    );
}

// ---------------------------------------------------------------------------
// Linked rate plans: each priced from the base plan by its own adjustment
// ---------------------------------------------------------------------------

/**
 * The example night's base plan with the two plans linked to it: a sales
 * channel's surcharge and the non-refundable rate's discount, each landing on
 * the property's rounding step.
 */
export function RatePlanSketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const money = useMoney(locale);
    const signedPct = useSignedPercent(locale);
    const base = exampleNightPrice(now);
    const plans: { label: string; pct: number | null }[] = [
        { label: t.welcome.ratePlanBase, pct: null },
        { label: t.welcome.ratePlanChannel, pct: EXAMPLE_RATE_PLANS.channelPct },
        { label: t.welcome.ratePlanNonRefundable, pct: EXAMPLE_RATE_PLANS.nonRefundablePct },
    ];

    return (
        <div data-testid="rate-plan-sketch" className="rounded-md border bg-background px-3 py-1.5">
            <table aria-label={t.welcome.ratePlanSketchLabel} className="w-full text-xs">
                <tbody>
                    {plans.map(({ label, pct }, index) => (
                        <tr key={label} className={index > 0 ? "border-t border-border/70" : undefined}>
                            {/* The adjustment follows its plan's name and
                                drops under it where the tile is narrow. As a
                                column of its own it held the row to three
                                unbreakable words, which widened the whole
                                grid past a 320px phone under high contrast. */}
                            <th scope="row" className="py-1.5 pr-3 text-left font-normal">
                                <span className="inline-flex flex-wrap items-baseline gap-x-2.5">
                                    <span data-plan-part="name">{label}</span>
                                    {pct !== null && (
                                        <span data-plan-part="adjustment" className={`font-mono ${adjustmentTone(pct)}`}>
                                            {signedPct(pct)}
                                        </span>
                                    )}
                                </span>
                            </th>
                            <td className="text-right align-top font-mono whitespace-nowrap py-1.5">
                                {money.format(linkedPlanPrice(base, pct ?? 0))}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Minimum stay: a number per room type and night
// ---------------------------------------------------------------------------

/** The weekend's nights, by `Date.getDay()`: Friday, Saturday, Sunday. */
const WEEKEND_DAYS = [5, 6, 0];

/** The example weekend's minimum stay, room type by night, under the calendar's own moon mark. */
export function MinStaySketch({ now }: SketchProps) {
    const { t, locale } = useLanguage();
    const weekday = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: "short" }), [locale]);
    const weekend = previewNights(now).filter((night) => WEEKEND_DAYS.includes(night.date.getDay()));

    return (
        <div data-testid="min-stay-sketch" className="rounded-md border bg-background px-3 py-2">
            <table className="w-full table-fixed text-xs">
                <caption className="pb-1.5 text-left text-[11px] text-muted-foreground">
                    {t.welcome.minStaySketchLabel}
                </caption>
                <thead>
                    <tr>
                        <th scope="col" className="w-2/5 pb-1 text-left font-normal text-muted-foreground">
                            {t.welcome.previewRoomColumn}
                        </th>
                        {weekend.map((night) => (
                            <th
                                key={night.date.toISOString()}
                                scope="col"
                                className="pb-1 text-center font-normal text-muted-foreground"
                            >
                                {capitalise(weekday.format(night.date), locale)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {PREVIEW_ROOMS.map((room) => (
                        <tr key={room.key} className="border-t border-border/70">
                            <th scope="row" className="truncate py-1.5 text-left font-medium">
                                {t.welcome.previewRooms[room.key]}
                            </th>
                            {EXAMPLE_MIN_STAY[room.key].map((nights, index) => (
                                <td key={index} className="text-center font-mono">
                                    <Moon aria-hidden="true" className="mr-1 inline size-3 text-muted-foreground" />
                                    {nights}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
